// language.service.ts
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private languageSubject = new BehaviorSubject<string>('eng');
  public language$: Observable<string> = this.languageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  initLanguage() {
    const lang = isPlatformBrowser(this.platformId)
      ? (localStorage.getItem('lang') || 'eng')
      : 'eng';
    this.translate.use(lang);
    this.languageSubject.next(lang);
  }

  setLanguage(lang: string) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('lang', lang);
    }
    this.translate.use(lang);
    this.languageSubject.next(lang);

    if (isPlatformBrowser(this.platformId)) {
      const root = document.documentElement;
      if (lang === 'ara') {
        root.style.setProperty('--app-font-family', `'Arial', sans-serif`);
      } else {
        root.style.setProperty('--app-font-family', `'inter', sans-serif`);
      }
    }

    // Navigate to appropriate URL based on language
    this.navigateWithLanguage(lang);
  }

  private navigateWithLanguage(lang: string) {
    const currentUrl = this.router.url;
    let newUrl = currentUrl;

    if (lang === 'ara') {
      // Add 'ar' prefix if not already there
      if (!currentUrl.startsWith('/ar')) {
        if (currentUrl === '/') {
          newUrl = '/ar';
        } else {
          newUrl = '/ar' + currentUrl;
        }
      }
    } else {
      // Remove 'ar' prefix for English
      if (currentUrl.startsWith('/ar/')) {
        newUrl = currentUrl.substring(3);
      } else if (currentUrl === '/ar') {
        newUrl = '/';
      }
    }

    if (newUrl !== currentUrl) {
      this.router.navigateByUrl(newUrl);
    }
  }

  getCurrentLang(): string {
    const lang = this.translate.currentLang as any;
    return (typeof lang === 'function' ? lang() : lang) || 'eng';
  }

  /**
   * Helper method to get language-aware URL for navigation
   * If current language is Arabic, prepends '/ar', otherwise returns URL as is
   */
  getLanguageAwareUrl(url: string): string {
    const currentLang = this.getCurrentLang();
    const isArabic = currentLang === 'ara';

    // If URL already has /ar prefix, return as is
    if (url.startsWith('/ar/') || url === '/ar') {
      if (!isArabic) {
        return url.startsWith('/ar/') ? url.substring(3) : '/';
      }
      return url;
    }

    // If URL doesn't have /ar prefix
    if (isArabic && !url.startsWith('/ar')) {
      return '/ar' + (url.startsWith('/') ? url : '/' + url);
    }

    return url;
  }
}